import React, { useState } from "react";

export function SafetyWorkflowBanner() {
  const steps = ["Dry Run", "Preview", "Backup", "Confirmation", "Execution", "Audit Log", "Undo Available"];
  return (
    <div className="fa-card flex flex-wrap gap-2 p-3 text-xs">
      {steps.map((s, i) => (
        <span key={s} className="rounded-full bg-indigo-50 px-2 py-1 font-medium text-indigo-800">
          {i + 1}. {s}
        </span>
      ))}
    </div>
  );
}

export function CostEstimatorPanel({ cost }) {
  if (!cost) return null;
  return (
    <div className="fa-card grid gap-2 p-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
      <p><strong>Est. reads:</strong> {cost.reads?.toLocaleString()}</p>
      <p><strong>Est. writes:</strong> {cost.writes?.toLocaleString()}</p>
      <p><strong>Est. deletes:</strong> {cost.deletes?.toLocaleString()}</p>
      <p><strong>Est. storage:</strong> {(cost.storageBytes / 1024).toFixed(1)} KB</p>
      <p className="text-indigo-700"><strong>Est. cost:</strong> ${cost.estimatedTotalUsd?.toFixed(4)} USD</p>
    </div>
  );
}

export function OperationConfirmModal({
  open,
  title,
  confirmWord,
  preview,
  onCancel,
  onConfirm,
  executing,
  children,
}) {
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  if (!open) return null;

  const canExecute = text === confirmWord && !executing;

  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-2xl">
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-lg font-bold">{step === 1 ? title : "Confirm"}</h2>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-auto p-4 text-sm">
          {children}
          {step === 2 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase">Type {confirmWord}</label>
              <input
                className="w-full rounded-lg border px-3 py-2 font-mono"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" className="fa-btn" onClick={onCancel} disabled={executing}>Cancel</button>
          {step === 1 ? (
            <button type="button" className="fa-btn fa-btn-primary" disabled={!preview?.affectedDocuments} onClick={() => setStep(2)}>
              Continue
            </button>
          ) : (
            <button type="button" className="fa-btn fa-btn-danger" disabled={!canExecute} onClick={onConfirm}>
              {executing ? "Executing…" : "Execute"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function OperationProgressModal({ progress, label = "Processing…" }) {
  if (!progress) return null;
  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-md p-6 text-center">
        <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        <h2 className="text-lg font-bold">{label}</h2>
        <p className="mt-2 text-sm text-slate-600">{progress.message}</p>
        {progress.batch != null && (
          <p className="mt-1 font-mono text-indigo-700">Batch {progress.batch} of {progress.totalBatches}</p>
        )}
      </div>
    </div>
  );
}

export function OperationResultModal({ result, labels, onClose }) {
  if (!result) return null;
  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-md p-4">
        <h2 className="text-lg font-bold">Complete</h2>
        <div className="mt-3 space-y-1 text-sm">
          <p><strong>{labels.success}:</strong> {(result.updated ?? result.deleted ?? 0).toLocaleString()}</p>
          <p><strong>{labels.failed}:</strong> {(result.failed ?? 0).toLocaleString()}</p>
          <p><strong>Time:</strong> {result.seconds}s</p>
          {result.errors?.map((e, i) => (
            <p key={i} className="text-xs text-red-600">{e}</p>
          ))}
        </div>
        <button type="button" className="fa-btn fa-btn-primary mt-4" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export function RestorePreviewModal({ open, oldDoc, currentDoc, onClose, onRestore }) {
  if (!open) return null;
  const keys = new Set([...Object.keys(oldDoc || {}), ...Object.keys(currentDoc || {})]);
  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-3xl">
        <div className="border-b p-4"><h2 className="font-bold">Restore preview</h2></div>
        <div className="max-h-96 overflow-auto p-4">
          <table className="fa-table text-xs">
            <thead><tr><th>Field</th><th>Backup (restore to)</th><th>Current</th></tr></thead>
            <tbody>
              {[...keys].map((k) => {
                const a = JSON.stringify(oldDoc?.[k]);
                const b = JSON.stringify(currentDoc?.[k]);
                const diff = a !== b;
                return (
                  <tr key={k} className={diff ? "bg-amber-50" : ""}>
                    <td>{k}</td>
                    <td className="font-mono">{a?.slice(0, 80)}</td>
                    <td className="font-mono">{b?.slice(0, 80)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" className="fa-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="fa-btn fa-btn-primary" onClick={onRestore}>Restore</button>
        </div>
      </div>
    </div>
  );
}

export function ExportMatchingToolbar({ rows, collectionName, fields }) {
  const exportRows = rows.map((r) => ({
    id: r.id,
    data: r.after || r.before || r.data,
  }));
  const doExport = async (fmt) => {
    const { exportMatchingCsv, exportMatchingExcel, exportMatchingJson, exportMatchingPdf } = await import(
      "../services/exportMatchingService"
    );
    const opts = { fields, includeId: true, filename: `${collectionName}-matching.${fmt === "excel" ? "xlsx" : fmt}` };
    if (fmt === "csv") exportMatchingCsv(exportRows, opts);
    else if (fmt === "excel") exportMatchingExcel(exportRows, opts);
    else if (fmt === "json") exportMatchingJson(exportRows, opts);
    else if (fmt === "pdf") exportMatchingPdf(exportRows, { ...opts, title: `${collectionName} — matching documents` });
  };
  if (!rows?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <span className="text-xs font-semibold text-slate-500">Export matching:</span>
      {["csv", "excel", "json", "pdf"].map((f) => (
        <button key={f} type="button" className="fa-btn text-xs" onClick={() => doExport(f)}>{f.toUpperCase()}</button>
      ))}
    </div>
  );
}
